// frontend/src/components/reputation/GrowthOpportunitiesTab.jsx
// Version: 1.0 (growth-agents-stage3)
// Description: Screen for discovered growth opportunities (media, legal directories,
//              communities, expert quote queries), proof verification, and outreach drafts.

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useUiLanguage, perevesti } from '../../context/UiLanguageContext';
import {
  Sparkles,
  ExternalLink,
  Check,
  Copy,
  CheckCircle2,
  XCircle,
  Clock,
  AlertCircle,
  HelpCircle,
  RefreshCw,
  Search,
  Mail,
  ShieldCheck,
  Star,
  X,
} from 'lucide-react';

export const GrowthOpportunitiesTab = ({ tenant = 'fitenko' }) => {
  const { lang: rawLang, t: contextT } = useUiLanguage();
  const lang = rawLang || 'ru';
  const isEn = lang === 'en';
  const t = useCallback((k, v) => (rawLang ? contextT(k, v) : perevesti('ru', k, v)), [rawLang, contextT]);

  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  // Pitch Modal
  const [activePitchOpp, setActivePitchOpp] = useState(null);
  const [pitchCopied, setPitchCopied] = useState(false);

  // Discovery state
  const [discovering, setDiscovering] = useState(false);
  const [toastMessage, setToastMessage] = useState(null);

  const fetchOpportunities = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`/api/v1/reputation/opportunities?tenant=${tenant}`);
      setOpportunities(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to load opportunities');
      setOpportunities([]);
    } finally {
      setLoading(false);
    }
  }, [tenant]);

  useEffect(() => {
    fetchOpportunities();
  }, [fetchOpportunities]);

  const updateStatus = async (oppId, newStatus) => {
    try {
      await axios.patch(`/api/v1/reputation/opportunities/${oppId}/status`, {
        status: newStatus,
      });
      fetchOpportunities();
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  const runDiscovery = async () => {
    setDiscovering(true);
    try {
      const res = await axios.post(`/api/v1/reputation/opportunities/discover?tenant=${tenant}`, {
        budget: 0.0,
      });
      setToastMessage(res.data.reason || t('rep.opportunities.zero_budget_notice'));
    } catch (err) {
      setToastMessage(err?.message || 'Discovery request failed');
    } finally {
      setDiscovering(false);
      setTimeout(() => setToastMessage(null), 4000);
    }
  };

  const copyPitch = (draftText) => {
    if (!draftText) return;
    navigator.clipboard.writeText(draftText);
    setPitchCopied(true);
    setToastMessage(t('rep.opportunities.pitch_copied'));
    setTimeout(() => {
      setPitchCopied(false);
      setToastMessage(null);
    }, 2500);
  };

  const list = Array.isArray(opportunities) ? opportunities : [];
  const filteredList = list.filter((opp) => {
    if (statusFilter !== 'all' && opp.status !== statusFilter) return false;
    if (typeFilter !== 'all' && opp.opportunity_type !== typeFilter) return false;
    return true;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return {
          icon: <CheckCircle2 className="w-3 h-3 text-emerald-600" />,
          label: t('rep.opportunities.status_approved'),
          className: 'bg-emerald-50 text-emerald-800 border-emerald-200',
        };
      case 'in_progress':
        return {
          icon: <Clock className="w-3 h-3 text-sky-600" />,
          label: t('rep.opportunities.status_in_progress'),
          className: 'bg-sky-50 text-sky-800 border-sky-200',
        };
      case 'done':
        return {
          icon: <Check className="w-3 h-3 text-purple-600" />,
          label: t('rep.opportunities.status_done'),
          className: 'bg-purple-50 text-purple-800 border-purple-200',
        };
      case 'rejected':
        return {
          icon: <XCircle className="w-3 h-3 text-rose-600" />,
          label: t('rep.opportunities.status_rejected'),
          className: 'bg-rose-50 text-rose-800 border-rose-200',
        };
      case 'new':
      default:
        return {
          icon: <Sparkles className="w-3 h-3 text-amber-600" />,
          label: t('rep.opportunities.status_new'),
          className: 'bg-amber-50 text-amber-800 border-amber-200',
        };
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'media':
        return t('rep.opportunities.type_media');
      case 'community':
        return t('rep.opportunities.type_community');
      case 'directory':
        return t('rep.opportunities.type_directory');
      case 'expert_quote':
        return t('rep.opportunities.type_expert_quote');
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-8 text-center space-y-3">
        <RefreshCw className="w-6 h-6 animate-spin text-[#C8A653] mx-auto" />
        <div className="text-sm text-[#687077]">{t('rep.state.loading')}</div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toast Notification */}
      {toastMessage && (
        <div className="fixed bottom-6 right-6 z-50 bg-[#20231F] text-white px-4 py-2.5 rounded-xl shadow-lg text-xs font-medium flex items-center gap-2">
          <Check className="w-4 h-4 text-emerald-400" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header with Search Agent status */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white border border-[#E5E1D6] rounded-2xl p-4">
        <div>
          <h2 className="text-base font-semibold text-[#20231F] flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[#C8A653]" />
            <span>{t('rep.opportunities.title')}</span>
          </h2>
          <p className="text-xs text-[#687077] mt-0.5">{t('rep.opportunities.subtitle')}</p>
        </div>

        <div className="flex items-center gap-2">
          <div className="text-[11px] text-[#687077] max-w-xs text-right hidden lg:block">
            {t('rep.opportunities.zero_budget_notice')}
          </div>
          <button
            onClick={runDiscovery}
            disabled={discovering}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors cursor-pointer disabled:opacity-50"
          >
            <Search className="w-3.5 h-3.5 text-[#C8A653]" />
            <span>{discovering ? t('rep.opportunities.discovering') : t('rep.opportunities.btn_run_discovery')}</span>
          </button>
        </div>
      </div>

      {/* Filter Tabs: Status & Types */}
      <div className="flex flex-wrap items-center justify-between gap-3 bg-white border border-[#E5E1D6] rounded-2xl p-3">
        {/* Status Filters */}
        <div className="flex items-center gap-1 overflow-x-auto text-xs">
          {['all', 'new', 'approved', 'in_progress', 'done', 'rejected'].map((st) => (
            <button
              key={st}
              onClick={() => setStatusFilter(st)}
              className={`px-2.5 py-1 rounded-lg font-medium transition-colors cursor-pointer ${
                statusFilter === st
                  ? 'bg-[#20231F] text-white font-semibold'
                  : 'text-[#687077] hover:bg-[#F8F7F2]'
              }`}
            >
              {st === 'all' ? t('rep.opportunities.filter_all') : t(`rep.opportunities.status_${st}`)}
            </button>
          ))}
        </div>

        {/* Type Filters */}
        <div className="flex items-center gap-1 p-0.5 bg-[#F8F7F2] rounded-xl border border-[#E5E1D6] text-xs">
          {['all', 'media', 'community', 'expert_quote'].map((tp) => (
            <button
              key={tp}
              onClick={() => setTypeFilter(tp)}
              className={`px-2.5 py-0.5 rounded-lg font-medium transition-colors cursor-pointer ${
                typeFilter === tp ? 'bg-white text-[#20231F] shadow-2xs font-semibold' : 'text-[#687077]'
              }`}
            >
              {tp === 'all' ? t('rep.opportunities.filter_all') : getTypeLabel(tp)}
            </button>
          ))}
        </div>
      </div>

      {/* Opportunities Table */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl shadow-xs overflow-hidden">
        {filteredList.length === 0 ? (
          <div className="p-8 text-center text-[#687077] text-xs space-y-2">
            <Sparkles className="w-8 h-8 text-[#C8A653] mx-auto opacity-50" />
            <div>{t('rep.opportunities.empty')}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs border-collapse">
              <thead>
                <tr className="border-b border-[#E5E1D6] bg-[#F8F7F2] text-[#687077] text-[11px] font-semibold uppercase tracking-wider">
                  <th className="py-3 px-3.5">{t('rep.opportunities.col_platform')}</th>
                  <th className="py-3 px-3.5">{t('rep.opportunities.col_type')}</th>
                  <th className="py-3 px-3.5">{t('rep.opportunities.col_proof')}</th>
                  <th className="py-3 px-3.5">{t('rep.opportunities.col_benefit')}</th>
                  <th className="py-3 px-3.5">{t('rep.opportunities.col_status')}</th>
                  <th className="py-3 px-3.5 text-right">{t('rep.opportunities.col_pitch')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#F1EEE4]">
                {filteredList.map((opp) => {
                  const statusMeta = getStatusBadge(opp.status);
                  return (
                    <tr key={opp.id} className="hover:bg-[#FAF9F5] transition-colors">
                      {/* Platform & Link */}
                      <td className="py-3 px-3.5">
                        <div className="flex items-center gap-1.5">
                          <span className="font-semibold text-[#20231F]">{opp.title}</span>
                          {opp.url && (
                            <a
                              href={opp.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[#C8A653] hover:text-[#A68838]"
                            >
                              <ExternalLink className="w-3 h-3" />
                            </a>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 mt-0.5 text-[10px] text-[#687077]">
                          <span className="font-medium uppercase">{opp.audience_lang}</span>
                          <span>&middot;</span>
                          <span>{opp.region}</span>
                          <span>&middot;</span>
                          <span>{opp.cost_notes || (opp.is_paid ? 'Paid' : 'Standard')}</span>
                        </div>
                      </td>

                      {/* Type */}
                      <td className="py-3 px-3.5">
                        <span className="inline-block px-2 py-0.5 rounded text-[10px] font-semibold bg-[#F5EED8] text-[#8C6B1B] border border-[#E8DCB8]">
                          {getTypeLabel(opp.opportunity_type)}
                        </span>
                      </td>

                      {/* Proof URL with Verification Date */}
                      <td className="py-3 px-3.5 max-w-[200px]">
                        <div className="flex items-center gap-1 text-[11px]">
                          <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                          <a
                            href={opp.proof_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#20231F] hover:underline truncate"
                            title={opp.proof_url}
                          >
                            {opp.proof_url}
                          </a>
                        </div>
                        {opp.proof_verified_at && (
                          <div className="text-[10px] text-[#687077] mt-0.5">
                            {opp.proof_verified_at.slice(0, 10)}
                          </div>
                        )}
                      </td>

                      {/* Benefit Score & Reason */}
                      <td className="py-3 px-3.5 max-w-[240px]">
                        <div className="flex items-center gap-1 text-amber-500 font-semibold text-xs">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star
                              key={i}
                              className={`w-3 h-3 ${
                                i < opp.benefit_score ? 'fill-amber-400 text-amber-400' : 'text-slate-200'
                              }`}
                            />
                          ))}
                        </div>
                        <div className="text-[10px] text-[#687077] mt-0.5 leading-tight">
                          {opp.benefit_reason}
                        </div>
                      </td>

                      {/* Status */}
                      <td className="py-3 px-3.5">
                        <div className="space-y-1.5">
                          <span
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium border ${statusMeta.className}`}
                          >
                            {statusMeta.icon}
                            <span>{statusMeta.label}</span>
                          </span>

                          {opp.status === 'new' && (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => updateStatus(opp.id, 'approved')}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-800 hover:bg-emerald-200 transition-colors cursor-pointer"
                              >
                                {t('rep.opportunities.btn_approve')}
                              </button>
                              <button
                                onClick={() => updateStatus(opp.id, 'rejected')}
                                className="px-1.5 py-0.5 rounded text-[10px] font-medium bg-rose-100 text-rose-800 hover:bg-rose-200 transition-colors cursor-pointer"
                              >
                                {t('rep.opportunities.btn_reject')}
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                      {/* Pitch Action */}
                      <td className="py-3 px-3.5 text-right">
                        <button
                          onClick={() => setActivePitchOpp(opp)}
                          className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors cursor-pointer"
                        >
                          <Mail className="w-3 h-3 text-[#C8A653]" />
                          <span>{t('rep.opportunities.btn_copy_pitch')}</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pitch Modal */}
      {activePitchOpp && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white border border-[#E5E1D6] rounded-2xl shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden">
            {/* Modal Header */}
            <div className="p-4 border-b border-[#E5E1D6] flex items-center justify-between bg-[#F8F7F2]">
              <div>
                <div className="text-xs text-[#687077] uppercase tracking-wider font-semibold">
                  {t('rep.opportunities.pitch_modal_title', { title: activePitchOpp.title })}
                </div>
                {activePitchOpp.contact_email && (
                  <div className="text-xs text-[#20231F] font-mono mt-0.5">
                    {activePitchOpp.contact_email}
                  </div>
                )}
              </div>
              <button
                onClick={() => setActivePitchOpp(null)}
                className="p-1 rounded-lg text-[#687077] hover:bg-[#E5E1D6] transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-4 overflow-y-auto flex-1 text-xs">
              <textarea
                readOnly
                value={activePitchOpp.pitch_draft || ''}
                rows={14}
                className="w-full p-3 rounded-xl border border-[#E5E1D6] bg-[#FAF9F5] text-[#20231F] font-mono text-xs leading-relaxed focus:outline-none"
              />
            </div>

            {/* Modal Footer */}
            <div className="p-3 border-t border-[#E5E1D6] bg-[#F8F7F2] flex items-center justify-between">
              <span className="text-[11px] text-[#687077]">
                {t('rep.opportunities.pitch_modal_desc')}
              </span>
              <button
                onClick={() => copyPitch(activePitchOpp.pitch_draft)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold bg-[#20231F] text-white hover:bg-[#343833] transition-colors"
              >
                {pitchCopied ? (
                  <Check className="w-3.5 h-3.5 text-emerald-400" />
                ) : (
                  <Copy className="w-3.5 h-3.5 text-[#C8A653]" />
                )}
                <span>{t('rep.opportunities.btn_copy_pitch')}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GrowthOpportunitiesTab;
